import type NewRelicBrowser from 'new-relic-browser'
import newrelic from 'newrelic'
import { is404Error } from '../filtering'
import type { ReportErrorOptions } from '../types'
import { isServer } from '../utils'
import type { MonitoringProvider } from './types'

const reportToNewRelicServer = (
  error: Error | string,
  props?: Record<string, string | number>,
): void => {
  // New Relic server API requires Error objects
  const errorObj = typeof error === 'string' ? new Error(error) : error
  newrelic.noticeError(errorObj, props)
}

class ReportedError extends Error {
  customAttributes?: Record<string, string | number>

  constructor(
    error: Error | string,
    customAttributes?: Record<string, string | number>,
  ) {
    super(typeof error === 'string' ? error : error.message)
    if (error instanceof Error) {
      if (error.stack !== undefined) {
        this.stack = error.stack
      }
      this.name = error.name
    }
    if (customAttributes !== undefined) {
      this.customAttributes = customAttributes
    }
  }
}

const reportToBrowser = (
  error: Error | string,
  props?: Record<string, string | number>,
): void => {
  const errorObj = new ReportedError(error, props)
  if (typeof window !== 'undefined' && window.newrelic) {
    window.newrelic.noticeError(errorObj, props)
  }
}

export const createNewRelicProvider = (): MonitoringProvider => ({
  trackError(
    error: Error | string,
    props?: Record<string, string | number>,
    options?: ReportErrorOptions,
  ): void {
    // Auto-filter 404 errors unless skipAutoFilter is true
    if (!options?.skipAutoFilter && is404Error(error)) {
      return
    }

    if (isServer()) {
      reportToNewRelicServer(error, props)
    } else {
      reportToBrowser(error, props)
    }
  },

  addPageAction(name: string, props?: Record<string, string | number>): void {
    if (!isServer()) {
      window.newrelic?.addPageAction(name, props ?? {})
    }
  },
})

declare global {
  interface Window {
    newrelic?: typeof NewRelicBrowser
  }
}
